import { FormControl, FormHelperText, FormLabel, InputLabel, MenuItem, Select } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Company, filterVisibleCompanies, GetCompanies } from "src/apis/company";
import { getUserCompanyId } from "src/utils/auth";
import { Id } from "src/utils/types"

type CompanySelectProps = {
  isFilter?: boolean,
  companyId: Id,
  setCompanyId: (id: Id) => void;
  setError: (err: string) => void;
  setCompanyIdByUser?: boolean;
  helperText?: string;
  disabled?: boolean;
  useAllOption?: boolean;
}

const CompanySelect: FC<CompanySelectProps> = ({
  isFilter = false,
  companyId,
  setCompanyId,
  setError,
  setCompanyIdByUser = false,
  helperText,
  disabled = false,
  useAllOption = false,
}): JSX.Element => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const setCompanyIdRef = useRef(setCompanyId);

  const props = useMemo<object>(() => {
    if (!isFilter) return {};

    return {
      label: "Empresa"
    }
  }, [isFilter])

  const fetchCompanies = useCallback(async (): Promise<void> => {
    try {
      const res = await GetCompanies();

      const companiesList = Array.isArray(res?.data) ? filterVisibleCompanies(res.data) : [];

      setCompanies(companiesList);
      setError('');
    } catch (err: any) {
      setError(err?.message);
    }
  }, [setError]);

  useEffect(() => {
    setCompanyIdRef.current = setCompanyId;
  }, [setCompanyId]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (setCompanyIdByUser) {
      const id = getUserCompanyId();

      setCompanyIdRef.current(id);
    }
  }, [setCompanyIdByUser])

  return (
    <FormControl>
      {isFilter ? (
        <InputLabel id="company-select">Empresa</InputLabel>
      ) : (
        <FormLabel id="company-select">Empresa</FormLabel>
      )}

      <Select
        disabled={(setCompanyIdByUser && Boolean(companyId)) || disabled}
        labelId="company-select"
        variant="outlined"
        value={companyId ?? ''}
        onChange={(e) => {
          setCompanyId(e.target.value as string);
        }}
        sx={{
          width: isFilter ? '200px' : '100%'
        }}
        {...props}
      >
        {useAllOption && (
          <MenuItem key={-1} value={String(-1)}>
            Todas
          </MenuItem>
        )}
        {companies.map((company) => (
          <MenuItem key={company.id} value={String(company.id)}>
            {company.trade_name}
          </MenuItem>
        ))}
      </Select>

      {Boolean(helperText) && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

export default CompanySelect;
